import { TechLibraryDocumentModel } from '@cas/config/documents';
import {
  columnDefTechLibraryDownload,
  columnDefTechLibraryProductFamilies,
  columnDefTechLibraryProducts,
  columnDefTechLibraryReference,
  columnDefTechLibraryReleaseDate,
  columnDefTechLibraryTitle,
  columnDefTechLibraryType,
} from '@customer/components/tables/grid/columnDefs';
import { FilterGroup } from '@customer/filters/types';
import useFilters from '@customer/filters/useFilters';
import { useTableData } from '@customer/shared/hooks/useTableData';
import { TechLibraryQueryParams, useGetTechDocsQuery } from '@customer/store/apis/documentsApi';
import { useAppDispatch } from '@customer/store/hooks';
import { openSidePanel, SidePanel } from '@customer/store/slices/globalSlice';
import { ColDef } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const useTechLibrary = (noDocDesc: string) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const filters = useFilters<TechLibraryQueryParams>(FilterGroup.TechLibrary);

  const { data, ...requestRemainder } = useGetTechDocsQuery(filters, {
    refetchOnMountOrArgChange: 600,
  });

  const columns = useMemo<ColDef<TechLibraryDocumentModel>[]>(() => {
    return [
      { ...columnDefTechLibraryReleaseDate(t), maxWidth: 150, sort: 'desc' },
      { ...columnDefTechLibraryType(t), maxWidth: 180 },
      { ...columnDefTechLibraryProductFamilies(t), maxWidth: 170 },
      { ...columnDefTechLibraryProducts(t), maxWidth: 150 },
      { ...columnDefTechLibraryReference(t), maxWidth: 150 },
      { ...columnDefTechLibraryTitle(t) },
      { ...columnDefTechLibraryDownload(t), maxWidth: 150 },

      // Allow the description field to be searchable (combined with includeHiddenColumnsInQuickFilter = true)
      { headerName: 'desc', field: 'description', hide: true },
    ];
  }, [t]);

  const noResultsDescription = t(noDocDesc);

  const { hasData, noDataDescription } = useTableData<TechLibraryDocumentModel>({
    data: data?.items,
    ...requestRemainder,
    noResultsDescription,
  });

  const onRowClick = useCallback(
    (data: TechLibraryDocumentModel) => {
      dispatch(
        openSidePanel({
          sidePanel: SidePanel.TechnicalLibraryDoc,
          modelId: data.id.toString(),
        }),
      );
    },
    [dispatch],
  );

  return {
    columns,
    data,
    hasData,
    noDataDescription,
    noResultsDescription,
    onRowClick,
  };
};

export default useTechLibrary;
